import { useState } from "react";

type CheckResult = {
  ok: boolean;
  message: string;
  preview?: string;
  type: "success" | "error" | "warning" | "auth";
};

export default function CorsChecker() {
  const [url, setUrl] = useState(
    "https://www.gazette.gov.mv/iulaan"
  );

  const [token, setToken] = useState("");
  const [useToken, setUseToken] = useState(false);

  const [loading, setLoading] = useState(false);
  const [result, setResult] =
    useState<CheckResult | null>(null);


  async function checkCors() {
    setResult(null);

    if (!url.trim()) {
      setResult({
        ok: false,
        type: "error",
        message: "Please enter a URL",
      });
      return;
    }


    try {
      new URL(url);
    } catch {
      setResult({
        ok: false,
        type: "error",
        message: "Invalid URL",
      });
      return;
    }


    const controller = new AbortController();

    const timeout = setTimeout(
      () => controller.abort(),
      10000
    );


    setLoading(true);


    try {
      const headers: HeadersInit = {
        Accept:
          "text/html,application/json",
      };


      if (useToken && token.trim()) {
        headers.Authorization =
          `Bearer ${token.trim()}`;
      }


      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        headers,
        signal: controller.signal,
      });


      const contentType =
        response.headers.get("content-type") ??
        "";


      const text =
        await response.text();


      // Browser reached server
      // so CORS is OK


      if (
        response.status === 401 ||
        response.status === 403
      ) {
        setResult({
          ok: false,
          type: "auth",
          message:
            `🔒 Not Authorized\n\n` +
            `No CORS issues detected.\n` +
            `Status: ${response.status}\n` +
            `Type: ${contentType}`,
          preview: text.slice(0, 500),
        });

        return;
      }


      if (!response.ok) {
        setResult({
          ok: false,
          type: "warning",
          message:
            `⚠️ Server Error\n\n` +
            `No CORS issues detected.\n` +
            `Status: ${response.status}\n` +
            `Type: ${contentType}`,
          preview: text.slice(0, 500),
        });

        return;
      }


      setResult({
        ok: true,
        type: "success",
        message:
          `✅ No CORS issues\n\n` +
          `Status: ${response.status}\n` +
          `Type: ${contentType}`,
        preview: text.slice(0, 500),
      });


    } catch (error) {

      let message =
        "❌ Request failed";


      if (
        error instanceof DOMException &&
        error.name === "AbortError"
      ) {
        message =
          "⏱️ Request timeout";
      }
      else if (
        error instanceof TypeError
      ) {
        message =
          "❌ CORS blocked or network error";
      }


      setResult({
        ok: false,
        type: "error",
        message,
      });


    } finally {

      clearTimeout(timeout);
      setLoading(false);

    }
  }


  return (
    <div className="max-w-xl mx-auto p-6 space-y-5">

      <div>
        <h2 className="text-2xl font-bold">
          CORS Checker
        </h2>

        <p className="text-sm text-gray-500">
          Test if a website/API can be accessed from
          your React frontend.
        </p>
      </div>


      <div className="space-y-3">

        <input
          className="
            w-full border rounded-lg
            px-3 py-2
            focus:outline-none
            focus:ring-2 focus:ring-blue-500
          "
          value={url}
          onChange={(e) =>
            setUrl(e.target.value)
          }
          placeholder="https://example.com"
        />


        <label className="
          flex items-center gap-2
          text-sm
        ">

          <input
            type="checkbox"
            checked={useToken}
            onChange={(e) =>
              setUseToken(e.target.checked)
            }
          />

          Send Authorization Token

        </label>


        {useToken && (
          <input
            type="password"
            className="
              w-full border rounded-lg
              px-3 py-2
            "
            value={token}
            onChange={(e) =>
              setToken(e.target.value)
            }
            placeholder="Bearer token"
          />
        )}


        <button
          onClick={checkCors}
          disabled={loading}
          className="
            w-full
            rounded-lg
            bg-blue-600
            text-white
            py-2
            hover:bg-blue-700
            disabled:opacity-50
          "
        >
          {loading
            ? "Checking..."
            : "Check URL"}
        </button>

      </div>


      {result && (
        <div
          className={`
            rounded-lg p-4
            border

            ${
              result.type === "success"
                ? "bg-green-50 border-green-300"
                :
              result.type === "auth"
                ? "bg-yellow-50 border-yellow-300"
                :
              result.type === "warning"
                ? "bg-orange-50 border-orange-300"
                :
                "bg-red-50 border-red-300"
            }
          `}
        >

          <pre className="
            whitespace-pre-wrap
            text-sm
          ">
            {result.message}
          </pre>


          {result.preview && (
            <details className="mt-4">

              <summary className="
                cursor-pointer
                font-medium
              ">
                Response Preview
              </summary>


              <pre className="
                mt-2
                text-xs
                bg-black
                text-white
                rounded
                p-3
                overflow-auto
                max-h-72
              ">
                {result.preview}
              </pre>

            </details>
          )}

        </div>
      )}

    </div>
  );
}